import { getViemClients, } from "@bracket/contractor-next";

import {
	bigintize,
	getContractAddress,
	getContractWithABI,
	getTokenDefinition,
	type ContractName,
	type Address,
	type ValidChainId,
} from "@bracket/chainography";

import {
	isBlockHash,
	isContractAddress,
	type BlockHash,
	type TokenIdentifier,
	type ContractAddress,
} from "@bracket/types";


import type { Config } from "wagmi";


export declare type ApproveProps = {
	contract?: ContractAddress;
	spender?: ContractAddress|ContractName;
	token: TokenIdentifier;
	amount: number|bigint;
	key?: Address;
	config?: Config;
	chainId?: ValidChainId;
};



/**
 * Approves a spender to use a specified amount of tokens on behalf of the caller
 * @param {ApproveProps} props - The approval parameters
 * @param {ContractAddress} [props.contract] - The contract address (alternative to spender)
 * @param {ContractAddress|ContractName} [props.spender] - The address or contract name that will be approved to spend tokens
 * @param {TokenIdentifier} props.token - The token identifier to approve
 * @param {number|bigint} props.amount - The amount to approve (will be converted to proper decimals if number)
 * @param {Config} [props.config] - Optional Wagmi configuration
 * @returns {Promise<BlockHash>} The transaction hash of the approval
 * @throws {Error} If the approval fails or returns invalid results
 */
export const approve = async ({
	contract,
	spender,
	token,
	key,
	amount,
	config,
	chainId = 1,
}: ApproveProps): Promise<BlockHash> => {

	try {

		if(contract) {
			console.warn("@bracket/chainography/erc20::approve - contract is deprecated, use spender instead");
			spender = contract;
		}
		const tok = getTokenDefinition(token);
		const [pclient, wallet] = await getViemClients({ chainId: tok.chainId, config, key, });
		const [__, abi ] = getContractWithABI("ERC20", tok.chainId);
		const spenderAddr = isContractAddress(spender) ? spender : getContractAddress(spender);
		const bamount = typeof amount === "bigint" ? amount : bigintize(amount, tok);

		console.log("SIMULATE>", {
			address: tok.address,
			abi,
			account: wallet.account,
			functionName: "approve",
			args: [
				spenderAddr,
				bamount,
			]
		});
		const { request, result, } = await pclient.simulateContract({
			address: tok.address,
			abi,
			account: wallet.account,
			functionName: "approve",
			args: [
				spenderAddr,
				bamount,
			]
		});
		if(typeof result === "boolean") {
			console.log("WRITE CONTRACT>", request);
			const hash = await wallet.writeContract(request);
			console.log("WAIT FOR TRANSACTION RECEIPT>", hash);
			if(isBlockHash(hash)) {
				console.log("WAIT FOR TRANSACTION RECEIPT>", hash);
				await pclient.waitForTransactionReceipt({ hash, });
				return hash;
			}
			throw new Error("failed to return a blockhash");
		}
		throw new Error("simulation did not produce a boolean");
	} catch (err) {
		throw new Error(`@bracket/contractor/erc20:approve ${err instanceof Error ? err.message : "unknown error"}`);
	}
};
